import { FunctionComponent, useMemo, useCallback, JSX } from "react"
import { observer } from "mobx-react-lite"
import { FilterOperator, IFilter } from "@knowmax/genericlist-core"
import { fieldIdForOperator } from "@knowmax/genericlist-core"
import { useGenericListSettings, getParamId } from "@knowmax/genericlist-core"
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, SplitButton } from "@fluentui/react-components"
import { EqualCircleRegular, EqualOffRegular, ChevronRightRegular, ChevronLeftRegular, SearchRegular } from "@fluentui/react-icons"
import { makeStyles } from "@griffel/react"
import { tokens } from "@fluentui/tokens"

const useClasses = makeStyles({
  unicodeIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: tokens.colorNeutralForeground1,
  },
})

type TOperatorConfig = {
  operator: FilterOperator
  icon: () => JSX.Element
  ariaLabel: string
}

const UnicodeIcon: FunctionComponent<{ symbol: string, className?: string }> = ({ symbol, className }) => (
  <span className={className} aria-hidden="true">{symbol}</span>
)

const buildOperatorConfigs = (unicodeIconClass: string): TOperatorConfig[] => [
  { operator: FilterOperator.eq, icon: () => <EqualCircleRegular />, ariaLabel: 'Equals' },
  { operator: FilterOperator.ne, icon: () => <EqualOffRegular />, ariaLabel: 'Not equals' },
  { operator: FilterOperator.gt, icon: () => <ChevronRightRegular />, ariaLabel: 'Greater than' },
  { operator: FilterOperator.ge, icon: () => <UnicodeIcon symbol="≥" className={unicodeIconClass} />, ariaLabel: 'Greater or equal' },
  { operator: FilterOperator.lt, icon: () => <ChevronLeftRegular />, ariaLabel: 'Less than' },
  { operator: FilterOperator.le, icon: () => <UnicodeIcon symbol="≤" className={unicodeIconClass} />, ariaLabel: 'Less or equal' },
  { operator: FilterOperator.contains, icon: () => <SearchRegular />, ariaLabel: 'Contains' },
]

/** Offers operator options. Serializes and deserializes in query params of url. */
export const OperatorSelector: FunctionComponent<{ filter: IFilter, listId?: string }> = observer(({ filter, listId }) => {
  const classes = useClasses()
  const settings = useGenericListSettings()

  const filterparamid = useMemo(() => getParamId(filter.id, listId), [filter.id, listId])

  const operators = useMemo(() => filter.operators ?? [FilterOperator.eq], [filter.operators])

  const selected = useMemo(() => {
    return settings.params.get(fieldIdForOperator(filterparamid)) ?? filter.operator
  }, [filter.operator, filterparamid, settings.params])

  const operatorConfigs = useMemo(() => buildOperatorConfigs(classes.unicodeIcon), [classes.unicodeIcon])

  const getConfig = useCallback((op?: string) =>
    operatorConfigs.find(c => c.operator === op),
    [operatorConfigs]
  )

  const handleSelect = useCallback((operator: FilterOperator) => {
    const params = new URLSearchParams(settings.params)

    if (operator === filter.operator) {
      params.delete(fieldIdForOperator(filterparamid))
    } else {
      params.set(fieldIdForOperator(filterparamid), operator)
    }

    settings.serialize(params)
  }, [filterparamid, filter.operator, settings.params])

  const selectedConfig = getConfig(selected)

  return (
    <>
      {operators.length > 1 &&
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <SplitButton
              appearance="subtle"
              icon={selectedConfig?.icon()}
              aria-label={selectedConfig?.ariaLabel}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {operators.map((op) => {
                const config = getConfig(op)
                if (!config) return null
                return (
                  <MenuItem
                    key={op}
                    icon={config.icon()}
                    aria-label={config.ariaLabel}
                    onClick={() => handleSelect(op)}
                  />
                )
              })}
            </MenuList>
          </MenuPopover>
        </Menu>
      }
    </>
  )
})